/**
 * Centralized Error Logging Service for Platform Admin
 *
 * This service provides comprehensive error tracking, logging, and reporting
 * for all platform admin operations.
 */

export enum ErrorSeverity {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  CRITICAL = 'CRITICAL',
}

export enum ErrorCategory {
  AUTHENTICATION = 'AUTHENTICATION',
  AUTHORIZATION = 'AUTHORIZATION',
  NETWORK = 'NETWORK',
  API = 'API',
  VALIDATION = 'VALIDATION',
  DATABASE = 'DATABASE',
  UNKNOWN = 'UNKNOWN',
}

export interface ErrorLogEntry {
  id: string;
  timestamp: Date;
  severity: ErrorSeverity;
  category: ErrorCategory;
  message: string;
  details?: any;
  stack?: string;
  endpoint?: string;
  httpStatus?: number;
  userId?: string;
  organizationId?: string;
  context?: Record<string, any>;
}

export interface ErrorStats {
  total: number;
  bySeverity: Record<ErrorSeverity, number>;
  byCategory: Record<ErrorCategory, number>;
  last24Hours: number;
  lastHour: number;
}

type ErrorListener = (error: ErrorLogEntry) => void;

class ErrorLoggerService {
  private errors: ErrorLogEntry[] = [];
  private maxErrors = 1000; // Keep last 1000 errors in memory
  private listeners: ErrorListener[] = [];
  private isInitialized = false;

  /**
   * Log an error with comprehensive details
   */
  logError(
    message: string,
    options: {
      severity?: ErrorSeverity;
      category?: ErrorCategory;
      details?: any;
      stack?: string;
      endpoint?: string;
      httpStatus?: number;
      userId?: string;
      organizationId?: string;
      context?: Record<string, any>;
    } = {}
  ): ErrorLogEntry {
    // Prevent logging errors during initialization to avoid infinite loops
    if (!this.isInitialized) {
      console.warn('[ErrorLogger] Attempted to log error before initialization:', message);
      return {
        id: 'init_error',
        timestamp: new Date(),
        severity: ErrorSeverity.MEDIUM,
        category: ErrorCategory.UNKNOWN,
        message,
      };
    }

    const error: ErrorLogEntry = {
      id: this.generateId(),
      timestamp: new Date(),
      severity: options.severity || this.inferSeverity(options.httpStatus),
      category: options.category || this.inferCategory(message, options.httpStatus),
      message,
      details: options.details,
      stack: options.stack,
      endpoint: options.endpoint,
      httpStatus: options.httpStatus,
      userId: options.userId,
      organizationId: options.organizationId,
      context: options.context,
    };

    // Add to in-memory store
    this.errors.unshift(error);

    // Trim to max size
    if (this.errors.length > this.maxErrors) {
      this.errors = this.errors.slice(0, this.maxErrors);
    }

    // Notify listeners
    this.notifyListeners(error);

    // Log to console based on severity
    this.logToConsole(error);

    // Store in localStorage for persistence
    this.persistToLocalStorage(error);

    return error;
  }

  /**
   * Log a fetch error with automatic extraction of details
   */
  async logFetchError(
    message: string,
    response: Response,
    options: {
      endpoint?: string;
      userId?: string;
      organizationId?: string;
      context?: Record<string, any>;
    } = {}
  ): Promise<ErrorLogEntry> {
    let details: any = null;

    try {
      const text = await response.text();
      try {
        details = JSON.parse(text);
      } catch {
        details = { responseText: text };
      }
    } catch {
      details = { message: 'Failed to parse error response' };
    }

    return this.logError(message, {
      severity: this.inferSeverity(response.status),
      category: this.inferCategory(message, response.status),
      details,
      endpoint: options.endpoint || response.url,
      httpStatus: response.status,
      userId: options.userId,
      organizationId: options.organizationId,
      context: options.context,
    });
  }

  /**
   * Log a JavaScript error/exception
   */
  logException(
    error: Error,
    options: {
      severity?: ErrorSeverity;
      category?: ErrorCategory;
      endpoint?: string;
      userId?: string;
      organizationId?: string;
      context?: Record<string, any>;
    } = {}
  ): ErrorLogEntry {
    return this.logError(error.message, {
      severity: options.severity || ErrorSeverity.HIGH,
      category: options.category || ErrorCategory.UNKNOWN,
      details: {
        name: error.name,
        message: error.message,
      },
      stack: error.stack,
      endpoint: options.endpoint,
      userId: options.userId,
      organizationId: options.organizationId,
      context: options.context,
    });
  }

  /**
   * Get all logged errors
   */
  getErrors(filters?: {
    severity?: ErrorSeverity;
    category?: ErrorCategory;
    since?: Date;
    limit?: number;
  }): ErrorLogEntry[] {
    let filtered = [...this.errors];

    if (filters?.severity) {
      filtered = filtered.filter((e) => e.severity === filters.severity);
    }

    if (filters?.category) {
      filtered = filtered.filter((e) => e.category === filters.category);
    }

    if (filters?.since) {
      const sinceDate = filters.since;
      filtered = filtered.filter((e) => e.timestamp >= sinceDate);
    }

    if (filters?.limit) {
      filtered = filtered.slice(0, filters.limit);
    }

    return filtered;
  }

  /**
   * Get error statistics
   */
  getStats(): ErrorStats {
    const now = new Date();
    const last24Hours = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    const lastHour = new Date(now.getTime() - 60 * 60 * 1000);

    const stats: ErrorStats = {
      total: this.errors.length,
      bySeverity: {
        [ErrorSeverity.LOW]: 0,
        [ErrorSeverity.MEDIUM]: 0,
        [ErrorSeverity.HIGH]: 0,
        [ErrorSeverity.CRITICAL]: 0,
      },
      byCategory: {
        [ErrorCategory.AUTHENTICATION]: 0,
        [ErrorCategory.AUTHORIZATION]: 0,
        [ErrorCategory.NETWORK]: 0,
        [ErrorCategory.API]: 0,
        [ErrorCategory.VALIDATION]: 0,
        [ErrorCategory.DATABASE]: 0,
        [ErrorCategory.UNKNOWN]: 0,
      },
      last24Hours: 0,
      lastHour: 0,
    };

    this.errors.forEach((error) => {
      stats.bySeverity[error.severity]++;
      stats.byCategory[error.category]++;

      if (error.timestamp >= last24Hours) {
        stats.last24Hours++;
      }

      if (error.timestamp >= lastHour) {
        stats.lastHour++;
      }
    });

    return stats;
  }

  /**
   * Clear all errors
   */
  clearErrors(): void {
    this.errors = [];
    localStorage.removeItem('platform_admin_errors');
  }

  /**
   * Clear errors older than specified date
   */
  clearOldErrors(before: Date): void {
    this.errors = this.errors.filter((e) => e.timestamp >= before);
  }

  /**
   * Subscribe to new errors
   */
  subscribe(listener: ErrorListener): () => void {
    this.listeners.push(listener);

    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  /**
   * Export errors as JSON
   */
  exportErrors(filters?: Parameters<typeof this.getErrors>[0]): string {
    const errors = this.getErrors(filters);
    return JSON.stringify(errors, null, 2);
  }

  /**
   * Load errors from localStorage on init
   */
  loadFromLocalStorage(): void {
    try {
      const stored = localStorage.getItem('platform_admin_errors');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          this.errors = parsed.map((e) => ({
            ...e,
            timestamp: new Date(e.timestamp),
          }));
        }
      }
    } catch (error) {
      console.error('Failed to load errors from localStorage:', error);
    }
  }

  // Private methods

  private generateId(): string {
    return `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private inferSeverity(httpStatus?: number): ErrorSeverity {
    if (!httpStatus) return ErrorSeverity.MEDIUM;

    if (httpStatus >= 500) return ErrorSeverity.CRITICAL;
    if (httpStatus === 401 || httpStatus === 403) return ErrorSeverity.HIGH;
    if (httpStatus >= 400) return ErrorSeverity.MEDIUM;
    return ErrorSeverity.LOW;
  }

  private inferCategory(message: string, httpStatus?: number): ErrorCategory {
    const lowerMessage = message.toLowerCase();

    if (httpStatus === 401 || lowerMessage.includes('auth') || lowerMessage.includes('login')) {
      return ErrorCategory.AUTHENTICATION;
    }

    if (httpStatus === 403 || lowerMessage.includes('permission') || lowerMessage.includes('forbidden')) {
      return ErrorCategory.AUTHORIZATION;
    }

    if (lowerMessage.includes('network') || lowerMessage.includes('fetch') || lowerMessage.includes('connection')) {
      return ErrorCategory.NETWORK;
    }

    if (lowerMessage.includes('validation') || lowerMessage.includes('invalid')) {
      return ErrorCategory.VALIDATION;
    }

    if (lowerMessage.includes('database') || lowerMessage.includes('query')) {
      return ErrorCategory.DATABASE;
    }

    if (httpStatus && httpStatus >= 400) {
      return ErrorCategory.API;
    }

    return ErrorCategory.UNKNOWN;
  }

  private logToConsole(error: ErrorLogEntry): void {
    const prefix = `[Platform Admin Error] [${error.severity}] [${error.category}]`;

    switch (error.severity) {
      case ErrorSeverity.CRITICAL:
      case ErrorSeverity.HIGH:
        console.error(prefix, error.message, error);
        break;
      case ErrorSeverity.MEDIUM:
        console.warn(prefix, error.message, error);
        break;
      case ErrorSeverity.LOW:
        console.log(prefix, error.message, error);
        break;
    }
  }

  private persistToLocalStorage(_error: ErrorLogEntry): void {
    try {
      // Keep last 100 errors in localStorage
      const errors = this.errors.slice(0, 100);
      localStorage.setItem('platform_admin_errors', JSON.stringify(errors));
    } catch (err) {
      console.error('Failed to persist error to localStorage:', err);
    }
  }

  private notifyListeners(error: ErrorLogEntry): void {
    this.listeners.forEach((listener) => {
      try {
        listener(error);
      } catch (err) {
        console.error('Error in error listener:', err);
      }
    });
  }
}

// Singleton instance
export const errorLogger = new ErrorLoggerService();

// Initialize after module is fully loaded
if (typeof window !== 'undefined') {
  // Use setTimeout to ensure this runs after all module initialization
  setTimeout(() => {
    // Mark as initialized to prevent recursive errors
    (errorLogger as any).isInitialized = true;

    // Load errors from localStorage on init
    try {
      errorLogger.loadFromLocalStorage();
    } catch (err) {
      console.warn('[ErrorLogger] Failed to load from localStorage:', err);
    }

    // Global error handler for unhandled errors
    window.addEventListener('error', (event) => {
      errorLogger.logException(event.error || new Error(event.message), {
        severity: ErrorSeverity.CRITICAL,
        context: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      });
    });

    // Global handler for unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      errorLogger.logException(
        event.reason instanceof Error ? event.reason : new Error(String(event.reason)),
        {
          severity: ErrorSeverity.HIGH,
          category: ErrorCategory.UNKNOWN,
          context: {
            type: 'unhandledRejection',
          },
        }
      );
    });
  }, 0);
}
